import { safeLocalStorageGet, safeLocalStorageSet } from './utils/storage';
import { v7 as uuidv7 } from 'uuid';

import { TERRIFIC_USER_ID } from '@shared/consts/terrific-user-id';
import { decodeBase64Json } from './decode-base64-json';
import { ANALYTICS_EVENTS_URL } from './events-url';

const TERRIFIC_META_KEY = 'terrific_meta';

declare global {
  interface Window {
    TerrificAnalyticsConfig?: TerrificConfig;
  }
}

export interface TerrificConfig {
  storeId: string;
}

export interface AnalyticsSdkProduct {
  id?: string;
  variantId?: string;
  title?: string;
  price?: number;
  sku?: string;
  quantity?: number;
}

let config: TerrificConfig | null = null;

function logger(...message: any[]): void {
  const debugFromStorage = safeLocalStorageGet('terrific_debug', () => {}) === 'true';
  if (debugFromStorage) {
    console.log('[Terrific Debug]', ...message);
  }
}

const PARENT_USER_ID_REQUEST_TYPE = 'terrific-analytics-user-id-request';
const PARENT_USER_ID_RESPONSE_TYPE = 'terrific-analytics-user-id-response';
const PARENT_REQUEST_ATTEMPTS = 3;
const PARENT_REQUEST_TIMEOUT_MS = 600;

function isInsideIframe(): boolean {
  try {
    return window.top !== window.self;
  } catch {
    return true;
  }
}

function timeout(ms: number): Promise<null> {
  return new Promise((resolve) => setTimeout(() => resolve(null), ms));
}

function parentPostMessageUserId(signal: AbortSignal): Promise<string | null> {
  return new Promise((resolve) => {
    const requestId = uuidv7();

    const onMessage = (event: MessageEvent): void => {
      const data = event.data;
      if (
        data &&
        typeof data === 'object' &&
        data.type === PARENT_USER_ID_RESPONSE_TYPE &&
        data.requestId === requestId &&
        typeof data.terrificUserId === 'string' &&
        data.terrificUserId
      ) {
        resolve(data.terrificUserId);
      }
    };

    window.addEventListener('message', onMessage, { signal });
    signal.addEventListener('abort', () => resolve(null), { once: true });

    try {
      window.parent?.postMessage({ type: PARENT_USER_ID_REQUEST_TYPE, requestId }, '*');
    } catch (error) {
      logger('Error requesting Terrific user id from parent frame:', error);
      resolve(null);
    }
  });
}

async function requestUserIdFromParent(timeoutMs: number): Promise<string | null> {
  const controller = new AbortController();
  const userId = await Promise.race([
    parentPostMessageUserId(controller.signal),
    timeout(timeoutMs),
  ]);
  controller.abort();
  return userId;
}

async function resolveTerrificUserId(): Promise<string> {
  const cached = safeLocalStorageGet(TERRIFIC_USER_ID, logger);
  if (cached) {
    return cached;
  }
  if (isInsideIframe()) {
    for (let attempt = 0; attempt < PARENT_REQUEST_ATTEMPTS; attempt++) {
      const parentUserId = await requestUserIdFromParent(PARENT_REQUEST_TIMEOUT_MS);
      if (parentUserId) {
        safeLocalStorageSet(TERRIFIC_USER_ID, parentUserId, logger);
        return parentUserId;
      }
    }
  }
  const generated = uuidv7();
  safeLocalStorageSet(TERRIFIC_USER_ID, generated, logger);
  return generated;
}

let userIdPromise: Promise<string> | null = null;

function getTerrificUserId(): Promise<string> {
  return (userIdPromise ??= resolveTerrificUserId());
}

const getTerrificClickIdFromUrl = (): string | null => {
  try {
    return new URLSearchParams(window.location.search).get('terrificClickId');
  } catch {
    return null;
  }
};

const getTerrificMetaFromUrl = (): string | null => {
  try {
    return new URLSearchParams(window.location.search).get('terrific_meta');
  } catch {
    return null;
  }
};

function sendEvent(eventName: string, eventData: Record<string, any> = {}): void {
  if (!config) {
    logger('Error: Analytics not initialized');
    throw new Error('Terrific Analytics not initialized. Call TerrificAnalytics.init() first.');
  }

  sendEventAsync(config, eventName, eventData);
}

async function sendEventAsync(
  currentConfig: TerrificConfig,
  eventName: string,
  eventData: Record<string, any>
): Promise<void> {
  const userId = await getTerrificUserId();

  const terrificClickId = eventData.terrificClickId ?? getTerrificClickIdFromUrl();
  const storeId = currentConfig.storeId || 'unknown';

  const rawMeta =
    eventData.terrific_meta ??
    getTerrificMetaFromUrl() ??
    safeLocalStorageGet(TERRIFIC_META_KEY, logger);
  if (rawMeta) {
    safeLocalStorageSet(TERRIFIC_META_KEY, rawMeta, logger);
  }
  const internalMetadata = decodeBase64Json<{ currency?: string }>(rawMeta);

  delete eventData.terrificClickId;
  delete eventData.terrific_meta;

  const payload = {
    type: eventName,
    userId: userId,
    internalUserId: userId,
    externalUserId: eventData.metadata?.externalUserId ?? null,
    storeId,
    url: window.location.href,
    timestamp: new Date().toISOString(),
    pixelSource: 'custom-pixel',
    ...eventData,
    ...(internalMetadata ? { internalMetadata } : {}),
    sessionId: terrificClickId ?? eventData.sessionId ?? null,
  };

  logger('Sending event:', payload);

  fetch(ANALYTICS_EVENTS_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  }).catch((error) => {
    logger('Error sending event:', error);
  });
}

const TerrificAnalytics = {
  init(configuration: TerrificConfig): void {
    logger('Initializing Terrific Analytics with config:', configuration);
    config = configuration;
  },

  trackPageViewed(data: {
    terrificClickId?: string;
    platformType: string;
    url?: string;
    metadata?: any;
  }): void {
    logger('Tracking page view:', data);
    sendEvent('PageViewed', data);
  },

  trackCheckoutStarted(data: {
    terrificClickId?: string;
    platformType: string;
    cartId: string;
    checkoutId?: string;
    orderId?: string;
    totalPrice?: number;
    subTotalPrice?: number;
    products?: AnalyticsSdkProduct[];
    metadata?: object;
  }): void {
    logger('Tracking checkout started:', data);
    sendEvent('CheckoutStarted', data);
  },

  trackCheckoutCompleted(data: {
    terrificClickId?: string;
    platformType: string;
    cartId: string;
    checkoutId?: string;
    orderId?: string;
    totalPrice?: number;
    subTotalPrice?: number;
    products?: AnalyticsSdkProduct[];
    metadata?: object;
  }): void {
    logger('Tracking checkout completed:', data);
    sendEvent('CheckoutCompleted', data);
  },

  trackProductViewed(data: {
    terrificClickId?: string;
    platformType: string;
    product: AnalyticsSdkProduct;
    metadata?: object;
  }) {
    logger('Tracking product viewed:', data);
    sendEvent('ProductViewed', data);
  },

  trackProductAddedToCart(data: {
    terrificClickId?: string;
    platformType: string;
    cartId: string;
    totalPrice?: number;
    product: AnalyticsSdkProduct;
    metadata?: object;
  }): void {
    logger('Tracking product added to cart:', data);
    sendEvent('ProductAddedToCart', data);
  },

  trackProductRemovedFromCart(data: {
    terrificClickId?: string;
    platformType: string;
    cartId: string;
    totalPrice?: number;
    product: AnalyticsSdkProduct;
    metadata?: object;
  }): void {
    logger('Tracking product removed from cart:', data);
    sendEvent('ProductRemovedFromCart', data);
  },
};

if (typeof window !== 'undefined' && window.TerrificAnalyticsConfig) {
  logger('Found window config, auto-initializing Terrific Analytics');
  TerrificAnalytics.init(window.TerrificAnalyticsConfig);
}

export default TerrificAnalytics;
